// /**
//  * VIP Standalone Field Resolvers
//  *
//  * These utilities abstract the difference between:
//  *   - Standalone VIP bookings (isStandaloneVip: true) → uses standard booking fields
//  *   - Legacy add-on VIP bookings → uses vipArrival*/vipDeparture* prefixed fields
//  *   - Normal bookings (transfer/BTH/car-rental) → uses standard booking fields
//  *
//  * By calling these resolvers, consuming code never needs to know which field naming
//  * convention is in use — it always gets back a normalized object with standard names.
//  *
//  * IMPORTANT: These resolvers are READ-ONLY helpers. For WRITE operations (API calls),
//  * see the action helpers in actions/booking.js that route to the correct endpoint.
//  *
//  * NOTE: This project uses react-scripts 3.0.1 which does NOT support optional chaining (?.).
//  * All property accesses must use && guards instead.
//  */

// // ─── Dispatch Fields ────────────────────────────────────────────────────────
// /**
//  * Resolves dispatch-related fields for a booking.
//  *
//  * For normal bookings and standalone VIP: reads from standard fields.
//  * For legacy add-on VIP: reads from vipArrival*/vipDeparture* prefixed fields.
//  *
//  * @param {Object} booking - The booking object from API response
//  * @param {'arrival'|'departure'|null} leg - Which VIP leg (only for legacy add-on)
//  * @returns {Object} Normalized dispatch fields
//  */
export const resolveDispatchFields = (booking, leg = null) => {
  if (!booking) return {}

  // Standalone VIP → use vipArrival*/vipDeparture* greeter fields based on leg
  if (booking.isStandaloneVip) {
    const prefix =
      booking.vipServiceType === 'departure' ? 'vipDeparture' : 'vipArrival'
    return {
      dispatch: booking.dispatch,
      dispatchStatus: booking.dispatchStatus,
      dispatchDate: booking.dispatchDate,
      serviceNotes: booking.serviceNotes,
      serviceNotesTemp: booking.serviceNotesTemp,
      greeter: booking[prefix + 'Greeter'],
      greeterTemp: booking[prefix + 'GreeterTemp'],
      greeterStatus: booking[prefix + 'GreeterStatus'],
      manualDispatch: booking.manualDispatch,
      buyingPrice:
        booking.dispatchRateInfo && booking.dispatchRateInfo.buyingPrice,
      payoutInfo:
        booking.dispatchRateInfo && booking.dispatchRateInfo.payoutInfo,
      partnerCurrency:
        booking.dispatchRateInfo && booking.dispatchRateInfo.currency,
    }
  }

  // Normal booking → standard fields
  if (booking.type !== 'vip_meet_greet') {
    return {
      dispatch: booking.dispatch,
      dispatchStatus: booking.dispatchStatus,
      dispatchDate: booking.dispatchDate,
      serviceNotes: booking.serviceNotes,
      serviceNotesTemp: booking.serviceNotesTemp,
      greeter: booking.greeter,
      greeterTemp: booking.greeterTemp,
      greeterStatus: booking.greeterStatus,
      manualDispatch: booking.manualDispatch,
      buyingPrice:
        booking.dispatchRateInfo && booking.dispatchRateInfo.buyingPrice,
      payoutInfo:
        booking.dispatchRateInfo && booking.dispatchRateInfo.payoutInfo,
      partnerCurrency:
        booking.dispatchRateInfo && booking.dispatchRateInfo.currency,
    }
  }

  // Legacy add-on VIP → prefixed fields
  const prefix = leg === 'arrival' ? 'vipArrival' : 'vipDeparture'
  return {
    dispatch: booking[prefix + 'Dispatch'],
    dispatchStatus: booking[prefix + 'DispatchStatus'],
    dispatchDate: booking[prefix + 'DispatchDate'],
    serviceNotes: booking[prefix + 'ServiceNotes'],
    serviceNotesTemp: booking[prefix + 'ServiceNotesTemp'],
    greeter: booking[prefix + 'Greeter'],
    greeterTemp: booking[prefix + 'GreeterTemp'],
    greeterStatus: booking[prefix + 'GreeterStatus'],
    manualDispatch: booking[prefix + 'ManualDispatch'],
    buyingPrice:
      booking.dispatchRateInfo &&
      booking.dispatchRateInfo[prefix + 'BuyingPrice'],
    payoutInfo:
      booking.dispatchRateInfo &&
      booking.dispatchRateInfo[prefix + 'PayoutInfo'],
    partnerCurrency: booking[prefix + 'PartnerCurrency'],
  }
}

// ─── "Primary" dispatch (used by components that don't know the leg) ────────
/**
 * Returns the "primary" VIP dispatch fields. Used by components that receive
 * a single vipDispatch / vipDispatchStatus without knowing arrival vs departure.
 *
 * For standalone VIP: returns standard fields (same as resolveDispatchFields).
 * For legacy add-on: returns the top-level vipDispatch / vipDispatchStatus fields.
 *
 * @param {Object} booking - The booking object
 * @returns {Object} Primary dispatch fields
 */
export const resolvePrimaryVipDispatch = booking => {
  if (!booking) return {}

  if (booking.type !== 'vip_meet_greet' || booking.isStandaloneVip) {
    return {
      dispatch: booking.dispatch,
      dispatchStatus: booking.dispatchStatus,
      dispatchDate: booking.dispatchDate,
      serviceNotes: booking.serviceNotes,
    }
  }

  // Legacy: top-level vipDispatch fields (not arrival/departure specific)
  return {
    dispatch: booking.vipDispatch,
    dispatchStatus: booking.vipDispatchStatus,
    dispatchDate: booking.vipDispatchDate,
    serviceNotes: booking.vipServiceNotes,
  }
}

// ─── Service Status (rate, payment, etc.) ───────────────────────────────────
/**
 * Resolves service-level status fields (rate, currency, status, paymentStatus, etc.)
 *
 * For normal bookings and standalone VIP: reads from booking root.
 * For legacy add-on VIP: reads from meetAndAssist or vipService nested objects.
 *
 * @param {Object} booking - The booking object
 * @param {'arrival'|'departure'|null} leg - Which VIP leg
 * @returns {Object} Normalized service status fields
 */
export const resolveServiceStatus = (booking, leg = null) => {
  if (!booking) return {}

  // Normal booking OR standalone VIP → booking root fields
  if (booking.type !== 'vip_meet_greet' || booking.isStandaloneVip) {
    return {
      rate: booking.rate,
      currency: booking.currency,
      status: booking.status,
      paymentStatus: booking.paymentStatus,
      payoutInfo: booking.payoutInfo,
      intentStatus: booking.intentStatus,
      isManualPaid: booking.isManualPaid,
      paid: booking.paid,
    }
  }

  // Legacy add-on VIP → meetAndAssist / vipService nested objects
  const maLeg =
    leg === 'arrival'
      ? booking.meetAndAssist && booking.meetAndAssist.arrival
      : booking.meetAndAssist && booking.meetAndAssist.departure
  const vsLeg =
    leg === 'arrival'
      ? booking.vipService && booking.vipService.arrival
      : booking.vipService && booking.vipService.departure
  const source = maLeg || vsLeg || {}

  return {
    rate: source.rate,
    currency: source.currency,
    status: source.status,
    paymentStatus: source.paymentStatus,
    payoutInfo: source.payoutInfo,
    intentStatus: source.intentStatus,
    isManualPaid: source.isManualPaid,
    paid: source.paid,
  }
}

// ─── Flight Info ────────────────────────────────────────────────────────────
/**
 * Resolves flight info for a VIP booking leg.
 *
 * For standalone VIP: reads from arrivalFlightInfo / departureFlightInfo on booking.
 * For legacy add-on: reads from meetAndAssist.arrival / meetAndAssist.departure (arrivalInfo/departureInfo).
 *
 * @param {Object} booking - The booking object
 * @param {'arrival'|'departure'|null} leg - Which leg
 * @returns {Object|null} Flight info object
 */
export const resolveFlightInfo = (booking, leg = null) => {
  if (!booking) return null

  if (booking.isStandaloneVip) {
    return leg === 'departure'
      ? booking.departureFlightInfo
      : booking.arrivalFlightInfo
  }

  // Legacy: from meetAndAssist
  const ma = booking.meetAndAssist
  if (leg === 'departure') {
    return (ma && ma.departure) || (ma && ma.departureInfo)
  }
  return (ma && ma.arrival) || (ma && ma.arrivalInfo)
}

// ─── VIP Passengers ─────────────────────────────────────────────────────────
/**
 * @param {Object} booking
 * @returns {Array} Passenger array
 */
const VIP_PAX_TYPE_ORDER = { adult: 0, child: 1, infant: 2 }
const sortVipPassengers = arr =>
  [...(arr || [])].sort((a, b) => {
    const ta =
      VIP_PAX_TYPE_ORDER[(a.passengerType || 'adult').toLowerCase()] ?? 0
    const tb =
      VIP_PAX_TYPE_ORDER[(b.passengerType || 'adult').toLowerCase()] ?? 0
    return ta - tb
  })

export const resolveVipPassengers = booking => {
  if (!booking) return []

  if (booking.isStandaloneVip) {
    return sortVipPassengers(booking.vipPassengers)
  }

  // Add-on VIP: Bookings.vipPassengers is authoritative after admin/client edits
  // (same as standalone). meetAndAssist.passengers is the creation-time snapshot.
  if (Array.isArray(booking.vipPassengers) && booking.vipPassengers.length) {
    return sortVipPassengers(booking.vipPassengers)
  }

  return (booking.meetAndAssist && booking.meetAndAssist.passengers) || []
}

// ─── Cancellation Policy ────────────────────────────────────────────────────
/**
 * @param {Object} booking
 * @returns {Object|null}
 */
export const resolveCancellationPolicy = booking => {
  if (!booking) return null

  if (booking.isStandaloneVip) {
    return booking.cancellationPolicy
  }

  return (
    (booking.meetAndAssist && booking.meetAndAssist.cancellationPolicy) ||
    booking.cancellationPolicy
  )
}

// ─── Travel Documents ───────────────────────────────────────────────────────
/**
 * @param {Object} booking
 * @returns {Array}
 */
export const resolveTravelDocuments = booking => {
  if (!booking) return []

  if (booking.isStandaloneVip) {
    return booking.travelDocuments || []
  }

  return (booking.meetAndAssist && booking.meetAndAssist.travelDocuments) || []
}

// ─── QA Checklist ───────────────────────────────────────────────────────────
/**
 * For standalone VIP: uses standard checklist field.
 * For legacy add-on: uses vipArrivalChecklist / vipDepartureChecklist.
 *
 * @param {Object} qaReport - The QA report object
 * @param {Object} booking - The booking object
 * @param {'arrival'|'departure'|null} leg
 * @returns {Object|null} Checklist object with { items, completed, name, UpdatedAt }
 */
export const resolveChecklist = (qaReport, booking, leg = null) => {
  if (!qaReport || !booking) return null

  if (booking.isStandaloneVip) {
    return qaReport.checklist || null
  }

  return leg === 'arrival'
    ? qaReport.vipArrivalChecklist
    : qaReport.vipDepartureChecklist
}

// ─── Change Requests ────────────────────────────────────────────────────────
/**
 * @param {Object} booking
 * @param {'arrival'|'departure'|null} leg
 * @returns {*} Change request data
 */
export const resolveChangeRequest = (booking, leg = null) => {
  if (!booking) return null

  if (booking.isStandaloneVip) {
    return booking.changeRequest
  }

  return leg === 'arrival'
    ? booking.changeRequestVipArrival
    : booking.changeRequestVipDeparture
}

// ─── Booking Lock Check ─────────────────────────────────────────────────────
/**
 * Checks if a VIP booking is locked (paid) and rates cannot be changed.
 *
 * For standalone VIP: checks booking-level paymentStatus.
 * For legacy add-on: checks vipService.arrival / vipService.departure.
 *
 * @param {Object} booking
 * @returns {boolean}
 */
export const isVipBookingLocked = booking => {
  if (!booking || booking.type !== 'vip_meet_greet') return false

  if (booking.isStandaloneVip) {
    return (
      booking.paymentStatus === 'paid' ||
      booking.intentStatus === 'succeeded' ||
      booking.isManualPaid === true
    )
  }

  // Legacy: check vipService
  const checkLeg = leg => {
    if (!leg) return false
    return (
      leg.paymentStatus === 'paid' ||
      leg.intentStatus === 'succeeded' ||
      leg.isManualPaid === true
    )
  }

  const vs = booking.vipService
  return checkLeg(vs && vs.arrival) || checkLeg(vs && vs.departure)
}

// ─── Write Path Helpers ─────────────────────────────────────────────────────
/**
 * Returns the correct field name for API write payloads (service notes, etc.)
 *
 * @param {Object} booking
 * @param {string} field - Standard field name (e.g., 'serviceNotes')
 * @param {'arrival'|'departure'|null} leg
 * @returns {string} The field name to use in the API payload
 */
export const getWriteFieldName = (booking, field, leg = null) => {
  if (booking.type !== 'vip_meet_greet' || booking.isStandaloneVip) {
    return field
  }

  // Legacy: prefix the field
  const prefix = leg === 'arrival' ? 'vipArrival' : 'vipDeparture'
  return prefix + field.charAt(0).toUpperCase() + field.slice(1)
}

// ─── API Endpoint Helpers ───────────────────────────────────────────────────
/**
 * Returns the correct dispatch endpoint for a VIP booking.
 *
 * @param {Object} booking
 * @param {string} bookingId
 * @param {'dispatch'|'manualDispatch'} action
 * @returns {string} API endpoint
 */
export const getVipDispatchEndpoint = (
  booking,
  bookingId,
  action = 'dispatch'
) => {
  if (booking.isStandaloneVip) {
    // Standalone: use standard booking dispatch endpoints
    return 'booking/' + bookingId + '/' + action
  }

  // Legacy: use VIP-specific endpoints
  const vipAction = action === 'dispatch' ? 'vipDispatch' : 'vipManualDispatch'
  return 'booking/' + bookingId + '/' + vipAction
}

/**
 * Builds the dispatch payload with the correct field names.
 *
 * @param {Object} booking
 * @param {Object} data - { invites, price, notes, localCurrency, isArrival }
 * @returns {Object} API payload
 */
export const buildDispatchPayload = (booking, data) => {
  if (booking.isStandaloneVip) {
    return {
      invites: data.invites,
      price: data.price,
      localRate: data.price,
      serviceNotes: data.notes,
      localCurrency: data.localCurrency,
    }
  }

  // Legacy
  return {
    invites: data.invites,
    price: data.price,
    vipServiceNotes: data.notes,
    localCurrency: data.localCurrency,
    isArrival: data.isArrival,
  }
}

// ─── Convenience: Is this a standalone VIP booking? ─────────────────────────
export const isStandaloneVip = booking => {
  return (
    booking &&
    booking.type === 'vip_meet_greet' &&
    booking.isStandaloneVip === true
  )
}

export const isLegacyAddonVip = booking => {
  return (
    booking && booking.type === 'vip_meet_greet' && !booking.isStandaloneVip
  )
}

export const isVipBooking = booking => {
  return booking && booking.type === 'vip_meet_greet'
}
